import React from 'react';
import { ValidatorComponent, ValidatorForm } from 'react-material-ui-form-validator';
import { useTheme } from '@mui/material/styles';
import { TextField } from '@mui/material';
import Label from '../Label';

// Theme-aware wrapper for EmailInputField
const EmailValidatorFieldWithTheme = React.forwardRef((props, ref) => {
    const theme = useTheme();
    return <EmailInputField {...props} theme={theme} ref={ref} />;
});

class EmailInputField extends ValidatorComponent {
    static defaultProps = {
        instantValidate: true,
    };

    componentDidMount() {
        // Register 'isEmail' rule for runtime validation
        try {
            ValidatorForm.addValidationRule('isEmail', (value) => {
                if (!value) return true; // allow empty and let required handle it
                const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                return emailPattern.test(value);
            });
        } catch (e) {
            // rule may already exist — ignore
        }
    }

    componentWillUnmount() {
        try {
            ValidatorForm.removeValidationRule('isEmail');
        } catch (e) {
            // ignore
        }
    }

    // Fallback expected by some versions of the validator
    validateDebounced(...args) {
        if (typeof ValidatorComponent.prototype.validate === 'function') {
            return ValidatorComponent.prototype.validate.apply(this, args);
        }
        return Promise.resolve(true);
    }

    // Custom email validation (kept for compatibility)
    validateEmail = (value) => {
        if (!value) return true; // Let required validator handle empty values
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailPattern.test(value);
    };

    // Override the validate method to handle email validation
    validate = (value, includeRequired = false) => {
        const { validators = [], required } = this.props;

        // Check if field is required (either via prop or in validators array)
        const isRequired = required || (validators && validators.includes('required'));

        // If field is not required and value is empty, skip validation
        if (!isRequired && !value) {
            this.setState({ isValid: true });
            return Promise.resolve(true);
        }

        // For all other cases, perform standard validation
        if (typeof super.validate === 'function') {
            return super.validate(value, includeRequired);
        }

        // Fallback if parent validate doesn't exist - just resolve as valid
        this.setState({ isValid: true });
        return Promise.resolve(true);
    };

    renderValidatorComponent() {
        const {
            value,
            onChange,
            disabled,
            placeholder,
            label,
            required,
            name,
            fullWidth = true,
            sx,
            theme,
            validators = [],
            errorMessages = [],
            ...rest
        } = this.props;

        const customInputStyles = {
            width: fullWidth ? '100%' : 'auto',
            '& .MuiOutlinedInput-root': {
                backgroundColor: theme?.palette?.background?.paper || '#fff',
                borderRadius: '5px',
                height: '40px',
                fontSize: '14px',
                '& fieldset': {
                    borderColor: theme?.palette?.textInput?.borderColor || '#e0e0e0',
                    boxShadow: `0px -0.04px 0px 2.7px ${theme?.palette?.textInput?.borderShadowColor || 'rgba(0,0,0,0.1)'}`,
                },
                '&:hover fieldset': {
                    borderColor: theme?.palette?.primary?.main || '#6b5bc8',
                },
                '&.Mui-focused fieldset': {
                    borderColor: theme?.palette?.primary?.main || '#6b5bc8',
                    borderWidth: '2px',
                    boxShadow: `0px -0.2px 0px 1px ${theme?.palette?.primary?.main || '#6b5bc8'}`,
                },
            },
            '& .MuiOutlinedInput-input': {
                padding: '10px 12px',
                color: theme?.palette?.text?.primary || '#000',
            },
            ...sx
        };

        const handleChange = (event) => {
            const newValue = event && event.target ? event.target.value : event;

            // Propagate change to consumer
            if (typeof onChange === 'function') {
                try {
                    onChange(event);
                } catch (e) {
                    try { onChange(newValue); } catch (err) { /* ignore */ }
                }
            }

            // Trigger validation immediately so error text updates while typing
            try {
                if (typeof this.validateDebounced === 'function') {
                    this.validateDebounced(newValue, true);
                } else if (typeof this.validate === 'function') {
                    this.validate(newValue, true);
                }
            } catch (e) {
                // swallow
            }

            // Also run local regex validation so error shows immediately
            try {
                const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                const isValidLocal = !newValue || emailPattern.test(newValue);
                const customMessage = (!isValidLocal && (errorMessages && errorMessages[0])) ? errorMessages[0] : null;
                this.setState({ isValid: isValidLocal, customErrorMessage: customMessage });
            } catch (err) {
                // ignore
            }
        };

        return (
            <div style={{ width: fullWidth ? '100%' : 'auto' }}>
                {label && (
                    <Label htmlFor={name} required={required}>{label}</Label>
                )}
                <TextField
                    name={name}
                    type="email"
                    value={value || ''}
                    onChange={handleChange}
                    placeholder={placeholder}
                    disabled={disabled}
                    fullWidth={fullWidth}
                    variant="outlined"
                    size="small"
                    sx={customInputStyles}
                    error={!this.state.isValid}
                    {...rest}
                />
                {this.errorText()}
            </div>
        );
    }

    errorText() {
        const { isValid, customErrorMessage } = this.state || {};
        const { theme } = this.props;

        const message = customErrorMessage || (!isValid ? this.getErrorMessage() : null);

        if (message) {
            return (
                <span style={{
                    color: theme?.palette?.error?.main || '#f44336',
                    fontSize: '12px',
                    marginTop: '4px',
                    display: 'block',
                    fontFamily: theme?.typography?.fontFamily || 'inherit',
                    fontWeight: 400,
                    lineHeight: 1.66
                }}>
                    {message}
                </span>
            );
        }

        return null;
    }
}

export default EmailValidatorFieldWithTheme;
